import { MemoryStick } from "lucide-react";
import type { ReactNode } from "react";

import type { MemoryAggregate, Scope } from "@busright/data-contract";

import { Drillable } from "@/components/Drillable";
import { MetricLabel } from "@/components/MetricTooltip";
import { formatMb, formatPct } from "@/lib/format";

// =================================================================================================
// TelMemoryFact — the used/total memory FACT for the current scope (fleet by default, one district
// when the scope switcher narrows it). Reuses the same used/total/free framing as Fleet Health's
// memory panel so the story is consistent everywhere memory shows up.
// =================================================================================================

export interface TelMemoryFactProps {
  readonly aggregate: MemoryAggregate | null;
  readonly scope: Scope;
}

export const TelMemoryFact = ({ aggregate, scope }: TelMemoryFactProps): ReactNode => {
  if (!aggregate || aggregate.avgUsedMb === null) {
    return <p className="card-subtitle">No memory readings yet for this scope.</p>;
  }
  return (
    <Drillable metricKey="memory.usedMb" scope={scope} label="Drill into per-device memory usage">
      <div className="fh-memory-fact">
        <div className="fh-memory-fact-head">
          <MemoryStick size={16} aria-hidden />
          <MetricLabel metricKey="memory.usedMb" label="Memory used (fact)" />
        </div>
        <div className="metric-strip">
          <div className="metric-chip">
            <span className="mc-label">
              <MetricLabel metricKey="memory.usedMb" label="Used" />
            </span>
            <span className="mc-value br-tabular-nums">{formatMb(aggregate.avgUsedMb)}</span>
          </div>
          <div className="metric-chip">
            <span className="mc-label">
              <MetricLabel metricKey="memory.totalMb" label="Total" />
            </span>
            <span className="mc-value br-tabular-nums">
              {aggregate.avgTotalMb === null ? "—" : formatMb(aggregate.avgTotalMb)}
            </span>
          </div>
          <div className="metric-chip">
            <span className="mc-label">
              <MetricLabel metricKey="memory.freeMb" label="Free" />
            </span>
            <span className="mc-value br-tabular-nums">
              {aggregate.avgFreeMb === null ? "—" : formatMb(aggregate.avgFreeMb)}
            </span>
          </div>
          <div className="metric-chip">
            <span className="mc-label">
              <MetricLabel metricKey="memory.usedPct" label="Used %" />
            </span>
            <span className="mc-value br-tabular-nums">
              {aggregate.avgUsedPct === null ? "—" : formatPct(aggregate.avgUsedPct)}
            </span>
          </div>
        </div>
      </div>
    </Drillable>
  );
};
